import React, { useState, useEffect } from 'react';
import {
  areEqual,
  isEmpty,
  requireFieldValidate,
  requireOccupationValidation,
} from '../../../utils/helpers';
import EditablePersonalInfo from '../../../atoms/EditablePersonalInfo';
import PanelStatsWithHeader from '../../../atoms/panels/PanelStatsWithHeader';
import { getBackgroundTheme, getTextColor } from '../../../style';
import Loader from '../../../atoms/LoaderStyled';
import _find from 'lodash/find';
import TrackLoadingToMixpanel from '../../common/TrackLoadingToMixPanel';
import { BODY_TYPE_OPTIONS } from '../../join/constants';
import { useDispatch, useSelector } from 'react-redux';
import { updateProfileAbout } from '../../../actions/profileActions';
import omit from 'lodash/omit';
import { RootState } from '../../../store';
import { FONT_FAMILIES } from 'theme/material/constants';

const generateBodytypeChildrenOptions = (type, data, mapFields, renderSelectedField) => {
  switch (type) {
    case 'Body Type':
      if (data.body_types.length > 1) {
        return data.body_types[0].label + ', ' + data.body_types[1].label;
      } else if (data.body_types.length === 1) {
        return data.body_types[0].label;
      } else {
        return '—';
      }
    case 'Children':
      if (Number.isInteger(data.children)) {
        return data.children > 0 ? renderSelectedField(mapFields.children, data.children) : '—';
      } else {
        return data.children;
      }
    default:
      return;
  }
};

const generateSmokingDrinkingOptions = (type, data, mapFields, renderSelectedField) => {
  switch (type) {
    case 'Smoking':
      if (Number.isInteger(data.smoking)) {
        return data.smoking > 0 ? renderSelectedField(mapFields.smoking, data.smoking) : '—';
      } else {
        return data.smoking;
      }
    case 'Drinking':
      if (Number.isInteger(data.drinking)) {
        return data.drinking > 0 ? renderSelectedField(mapFields.drinking, data.drinking) : '—';
      } else {
        return data.drinking;
      }
    default:
      return;
  }
};

const generateIncomeOptions = (data, mapFields, renderSelectedField) => {
  return Number.isInteger(data.income) ? renderSelectedField(mapFields.income, data.income) : data.income;
};

const generateNetWorthOptions = (data, mapFields, renderSelectedField) => {
  return Number.isInteger(data.net_worth) ? renderSelectedField(mapFields?.net_worth, data.net_worth) : data.net_worth;
};

const setBodyTypeVal = value => {
  return typeof value !== 'number' ? parseInt(value, 10) : value;
};

const setBodyTypeLabel = (item: { description: string; label: string }) => {
  return item.description ? item.description : item.label;
};

const findFields = (myInfo, fields, key) => {
  return myInfo[key] === '' && fields.find(field => field === key);
};

const setNonMandatoryFields = (nonManFields, data) => {
  const mapObj = {};

  for (const key in data) {
    if (data[key] || findFields(data, nonManFields, key)) {
      mapObj[key] = data[key];
    }
  }
  return mapObj;
};

const renderInfo = data => {
  return data || '—';
};

const PersonalInfo = (props: any): any => {
  /* manage and maintain the user about info state here and toggle between the
     readonly and editable component
  */
  const { myProfile, isLogin = false, acctProfile, ...rest } = props;
  const { isProcessing } = useSelector((state: RootState) => state.profile);
  const { map_fields: mapFields } = useSelector((state: RootState) => state.meta);

  const dispatch = useDispatch();
  const [aboutFields, setAboutFields] = useState(false);
  const [personalInfo, setPersonalInfo] = useState({
    body_types: [],
    height: '',
    children: 0,
    smoking: 0,
    drinking: 0,
    education: 0,
    occupation: null,
    income: 0,
    looking_male: false,
    looking_female: false,
    net_worth: 0,
  });
  const [errors, setErrors] = useState({});
  const [submitErrorCount, setSubmitErrorCount] = useState(0);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const isGenerous = myProfile.account_type === 'Generous';

  useEffect(() => {
    mapAllStates();

    return () => {
      setAboutFields(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myProfile]);

  useEffect(() => {
    if (isSubmitted && !isProcessing) {
      setAboutFields(false);
    }
  }, [isSubmitted, isProcessing]);

  const mapAllStates = () => {
    if (!myProfile) return;
    const {
      body_types,
      height,
      children,
      smoking,
      drinking,
      education,
      occupation,
      income,
      looking_female,
      looking_male,
      net_worth,
    } = myProfile;

    setPersonalInfo({
      body_types: mapBodyType(body_types),
      height: height,
      children: children,
      smoking: smoking,
      drinking: drinking,
      education: education,
      occupation: occupation,
      income: income,
      looking_female: looking_female,
      looking_male: looking_male,
      net_worth: net_worth,
    });
  };

  const validateOnChange = (fieldName, value) => {
    if (!isGenerous && fieldName === 'income') {
      //ignore validation for attrative and field is occupation, income
    } else {
      if (fieldName === 'occupation') {
        errors[fieldName] = requireOccupationValidation(fieldName, value, isGenerous);
      } else {
        errors[fieldName] = requireFieldValidate(fieldName, value);
      }

      setErrors(errors);
    }
  };

  const makeChange = (category, value, fieldName) => {
    let updatedValue = value;

    if (
      (category === 'looking_male' && value === false && personalInfo.looking_female === false) ||
      (category === 'looking_female' && value === false && personalInfo.looking_male === false)
    ) {
      updatedValue = false;
    } else if (['looking_male', 'looking_female'].includes(category)) {
      updatedValue = true;
    }

    if (fieldName === 'income') {
      updatedValue = value === 0 ? false : value;
    }

    //checking required validation here
    validateOnChange(fieldName, updatedValue);
    setPersonalInfo({
      ...personalInfo,
      [category]: value,
    });
  };

  const toggleEditable = e => {
    e.preventDefault();
    setAboutFields(!aboutFields);
  };

  const onChangeBodyType = (val, name) => {
    const currentBodyTypes = personalInfo.body_types;
    const isIncluded = _find(currentBodyTypes, val);

    if (!isIncluded && currentBodyTypes.length !== 1) {
      currentBodyTypes.push(val);
      setPersonalInfo({
        ...personalInfo,
        body_types: currentBodyTypes,
      });
      validateOnChange(name, currentBodyTypes);
    } else if (isIncluded) {
      const filteredArr = currentBodyTypes.filter(item => item.value !== val.value);

      setPersonalInfo({
        ...personalInfo,
        body_types: filteredArr,
      });
      validateOnChange(name, filteredArr);
    }
  };

  const onToggleChangeHandler = targetId => {
    makeChange(targetId, !personalInfo[targetId], 'interested');
  };

  const onToggleChildren = (id, value) => {
    makeChange(id, isNaN(value) ? value : parseInt(value), id);
  };

  const onChangeHandler = e => {
    const id = e.target.id;
    const value = e.target.value;

    makeChange(id, isNaN(value) || e.target.type !== 'select-one' ? value : parseInt(value), e.target.name);
  };

  const setNonMandatoryData = info => {
    const nonMandatoryFields = ['children', 'smoking', 'drinking', 'education', 'occupation', 'income'];

    return setNonMandatoryFields(nonMandatoryFields, info);
  };

  const validateBeforeFormSubmit = () => {
    let errorCount = 0;
    const personalInfoCopy = personalInfo;
    const requiredPersonalInfo = isGenerous
      ? omit(personalInfoCopy, ['looking_male', 'looking_female'])
      : omit(personalInfoCopy, ['looking_male', 'looking_female', 'income', 'net_worth']);

    for (const [fieldName, info] of Object.entries(requiredPersonalInfo)) {
      const updatedFieldName = fieldName === 'body_types' || fieldName === 'body_type' ? 'body_type' : fieldName;

      if (fieldName === 'occupation') {
        errors[updatedFieldName] = requireOccupationValidation(updatedFieldName, info, isGenerous);
      } else {
        errors[updatedFieldName] = requireFieldValidate(updatedFieldName, info);
      }

      !isEmpty(errors[updatedFieldName]) && errorCount++;
    }

    if (personalInfo.looking_male === false && personalInfo.looking_female === false) {
      errors['interested'] = requireFieldValidate('interested', false);
      errorCount++;
    }

    return { errors, errorCount };
  };

  const handleSubmit = e => {
    e.preventDefault();
    const { errors, errorCount } = validateBeforeFormSubmit();

    if (errorCount > 0) {
      setIsSubmitted(false);
      setSubmitErrorCount(errorCount);
      setErrors(errors);
    } else {
      setIsSubmitted(true);
      personalInfo.body_types.map(item => {
        BODY_TYPE_OPTIONS.find(type => type.value === item.value);
      });

      const nonManData = setNonMandatoryData(personalInfo);

      dispatch(
        updateProfileAbout({
          height: personalInfo.height,
          body_types: personalInfo.body_types,
          looking_female: personalInfo.looking_female,
          looking_male: personalInfo.looking_male,
          ...nonManData,
        })
      );
    }
  };

  const handleCancel = e => {
    // need to clear the fields if user select cancel button
    toggleEditable(e);
    mapAllStates();
    setErrors({});
    setSubmitErrorCount(0);
  };

  const mapBodyType = bTypes => {
    const renderDefaultBodyTypes = [];

    if (
      myProfile &&
      myProfile !== undefined &&
      bTypes &&
      bTypes !== undefined &&
      bTypes.data !== undefined &&
      bTypes.data.length > 0
    ) {
      bTypes.data.map(item => {
        renderDefaultBodyTypes.push({
          value: setBodyTypeVal(item.value),
          label: setBodyTypeLabel(item),
        });
      });
    }

    return renderDefaultBodyTypes;
  };

  const renderSelectedField = (field, selected) => {
    return field?.[selected] ?? null;
  };

  const renderData = () => {
    const commonData = [
      {
        label: 'Body Type',
        info: generateBodytypeChildrenOptions('Body Type', personalInfo, mapFields, renderSelectedField),
        dataId: 'body-type',
      },
      {
        label: 'Height',
        info: renderInfo(personalInfo.height),
        dataId: 'height',
      },
      {
        label: 'Children',
        info: generateBodytypeChildrenOptions('Children', personalInfo, mapFields, renderSelectedField),
      },
      {
        label: 'Smoking',
        info: generateSmokingDrinkingOptions('Smoking', personalInfo, mapFields, renderSelectedField),
      },
      {
        label: 'Drinking',
        info: generateSmokingDrinkingOptions('Drinking', personalInfo, mapFields, renderSelectedField),
      },
      {
        label: 'Education',
        info: renderInfo(personalInfo.education),
      },
      {
        label: 'Occupation',
        info: renderInfo(personalInfo.occupation),
      },
    ];

    if (acctProfile && acctProfile.hash_id === myProfile.hash_id && !isGenerous) {
      return commonData;
    }
    return [
      ...commonData,
      {
        label: 'Income',
        info: generateIncomeOptions(personalInfo, mapFields, renderSelectedField),
      },
      {
        label: 'Net Worth',
        info: generateNetWorthOptions(personalInfo, mapFields, renderSelectedField),
      },
    ];
  };

  return (
    <div>
      {aboutFields ? (
        <Loader active={isProcessing} background={getBackgroundTheme().personalInfo}>
          {isProcessing && <TrackLoadingToMixpanel page="Profile - saving about me info" />}
          <EditablePersonalInfo
            errors={errors}
            errorCount={submitErrorCount}
            bodyTypes={personalInfo.body_types}
            height={personalInfo.height}
            totalChildren={personalInfo.children}
            smoking={personalInfo.smoking}
            drinking={personalInfo.drinking}
            education={personalInfo.education}
            occupation={personalInfo.occupation}
            income={personalInfo.income}
            net_worth={personalInfo.net_worth}
            lookingMale={personalInfo.looking_male}
            lookingFemale={personalInfo.looking_female}
            onChangeHandler={onChangeHandler}
            mapFields={mapFields}
            className="profile__personal-info"
            title="About Me"
            handleSubmit={handleSubmit}
            handleCancel={handleCancel}
            onChangeBodyType={onChangeBodyType}
            onCheckboxChangeHandler={onToggleChangeHandler}
            onToggleChildren={onToggleChildren}
            isGenerous={isGenerous}
            gender={myProfile.gender}
            panelStyle="borderless"
            customTitleStyle={{
              fontWeight: 400,
              fontFamily: FONT_FAMILIES.secondary,
              lineHeight: '20px',
              letterSpacing: -0.2,
              ...getTextColor().DARK_4,
            }}
          />
        </Loader>
      ) : (
        <PanelStatsWithHeader
          className="profile__personal-info"
          title="About Me"
          data={renderData()}
          toggleEditable={toggleEditable}
          isLogin={isLogin}
          dataTestId="edit-about-info"
          panelStyle="borderless"
          customTitleStyle={{
            fontWeight: 400,
            fontFamily: FONT_FAMILIES.secondary,
            lineHeight: '20px',
            letterSpacing: -0.2,
            ...getTextColor().DARK_4,
          }}
          {...rest}
        />
      )}
    </div>
  );
};

export default React.memo(PersonalInfo, areEqual);
